import { CounselingTypeEnum } from 'app/Bookings/constants'
import {
  providerTypes,
  SCHEMA_ORG_ITEM_LIST_ORDER_UNORDERED,
  SeFriendlyPageType,
  specialtyValueMapping,
  specialtyValueMappingForBreadcrumbs,
  THRIVEWORKS_SCHEMA_HOME_URL,
  THRIVEWORKS_SCHEMA_ORGANIZATION_ID,
  THRIVEWORKS_SCHEMA_WEBSITE_ID,
} from './constants'
import { popularCities } from './HubStates/constants'
import { getFilterIdsByValue } from 'app/ProviderSearch/utils'
import { ProviderFilterTypes } from 'dataSource/ProviderSearch'
import {
  hasLocations,
  hasPsychiatry,
  stateAbbreviations,
} from './validateUsaState'
import credentials from 'common/ProviderCredentials/credentials'
import {
  getProviderProfileRoute,
  PROVIDER_SLOTS,
} from 'dataSource/ProviderSearch/routes'
import { convertHtmlToString, getPractitionerType } from 'utils/common'
import callApi from 'utils/callApi'
import addHours from 'date-fns/addHours'
import { INTAKE_BOOKING_WINDOW_HOURS } from 'constants/common'

/**
 * Resolves the FAQ templates for one page request.
 *
 * `faqData` is a module-scope constant shared by every request served by a warm
 * server instance, so this must never write back into it: substituting in place
 * consumes the `{{city}}`/`{{state}}` placeholders permanently and every later
 * request on that instance then renders — and publishes as FAQPage JSON-LD —
 * the first requested city's copy, including its licensure claim. Templates in,
 * new objects out.
 */
export const formatFaqForSefriendlyPages = (
  faqData: readonly FAQsPanelItem[],
  state: string,
  city?: string
): FAQsPanelItem[] => {
  const stateFullSpelling = stateAbbreviations[state]
  const stateHasPsychiatry = hasPsychiatry.includes(stateFullSpelling)
  const stateHasLocation = Object.keys(hasLocations).includes(stateFullSpelling)

  // State pages have no city, so the state stands in as the locality.
  const locality = city || stateFullSpelling

  const psychiatryContent = stateHasPsychiatry
    ? ` <a href="/child-therapy/">child therapy</a>/<a href="/therapy/teen-counseling/">teen counseling</a>, and <a href="/psychiatry/">psychiatry</a>/medication management.`
    : ` and <a href="/child-therapy/">child therapy</a>/<a href="/therapy/teen-counseling/">teen counseling</a>.`

  const stateLocationsContent = stateHasLocation
    ? ` Thriveworks has <a href="/find-counselor/">over 380 locations</a> across the US including a number of offices in ${stateFullSpelling}. We also offer video sessions to all ${locality} clients.`
    : ''

  const resolveTemplate = (template: string): string =>
    template
      .replaceAll('{{city}}', locality)
      .replaceAll('{{state}}', stateFullSpelling)
      .replaceAll('{{psychiatryContent}}', psychiatryContent)
      .replaceAll('{{stateLocationsContent}}', stateLocationsContent)

  return faqData.map((faq) => ({
    ...faq,
    question: resolveTemplate(faq.question),
    answer: resolveTemplate(faq.answer),
  }))
}

export const formatCity = (city: string): string => {
  return city.replaceAll(' ', '-').toLowerCase()
}

export const getNextAvailForProviders = async (
  providers: Provider[] | ListCardProvider[],
  bookingWindowHours: number = INTAKE_BOOKING_WINDOW_HOURS
): Promise<NextAvailableSlotInfo[]> => {
  const today = new Date()
  const daysToInTheFuture = 21
  const futureDate = new Date(
    today.setDate(today.getDate() + daysToInTheFuture)
  ).toISOString()

  const startTime = addHours(new Date(), bookingWindowHours)

  const availProviderData = providers?.map((provider) => ({
    providerId: provider.id,
    practiceId: provider.practice.id,
  }))

  const data = JSON.stringify({
    start: startTime.toISOString(),
    end: futureDate,
    for: availProviderData,
  })

  const nextAvailableSlotInfo: NextAvailableSlotInfo[] = await callApi({
    endpoint: PROVIDER_SLOTS,
    method: 'post',
    data,
  })

  return nextAvailableSlotInfo
}

export const setNextAvailForProviders = async (
  providers: ListCardProvider[],
  bookingWindowHours?: number
): Promise<ListCardProvider[]> => {
  const nextAvailData: NextAvailableSlotInfo[] = await getNextAvailForProviders(
    providers,
    bookingWindowHours
  )

  // Create a Map for fast lookup
  const slotMap = new Map(nextAvailData.map((item) => [item.providerId, item]))

  const updatedProvidersWithNextAvail = providers.map((provider) => {
    const matchingProviderSlot = slotMap.get(provider.id)

    return {
      ...provider,
      nextAvail: matchingProviderSlot ? [matchingProviderSlot] : [],
    }
  })

  return updatedProvidersWithNextAvail
}

export const getCounselingTypeIds = (
  type: string,
  groupedFilters: GroupedProviderFilter
): string[] => {
  const counselingTypesByProvider = {
    [providerTypes.THERAPISTS]: [
      CounselingTypeEnum.INDIVIDUAL,
      CounselingTypeEnum.COUPLES,
      CounselingTypeEnum.FAMILY,
    ],
    [providerTypes.PSYCHIATRISTS]: [CounselingTypeEnum.MEDICATION],
  }

  const counselingTypesValues = counselingTypesByProvider[type] || []

  const counselingTypeIds = getFilterIdsByValue(
    groupedFilters,
    ProviderFilterTypes.COUNSELING_TYPE,
    counselingTypesValues
  )

  return counselingTypeIds
}

export const getProviderInfo = (
  provider: Provider,
  requestOrigin?: string
): {
  providerFullName: string
  jobTitle: string
  providerProfileUrl: string
  providerMetaDescription: string
} => {
  const { firstName, lastName, title, practice } = provider
  const providerFullName = `${firstName} ${lastName}`

  const providerCredentials = credentials[title] || {}

  const fullCredentials = providerCredentials?.definition

  const jobTitle = `${fullCredentials} ${
    title !== fullCredentials ? ` (${title})` : ''
  }`

  const profileRoute = getProviderProfileRoute(provider)

  const origin =
    requestOrigin ??
    (typeof window !== 'undefined' ? window.location.origin : '')

  const providerProfileUrl = origin + profileRoute

  const practitonerType = getPractitionerType(title)
  const providerLocation = practice
    ? `${practice.city}, ${practice.state}`
    : null

  let providerMetaDescription = `Book a session with this provider`

  if (!!providerFullName && !!practitonerType && !!providerLocation) {
    providerMetaDescription = `${providerFullName} is a Thriveworks ${practitonerType} practicing in ${providerLocation}. Find a same-day or next-day session with ${providerFullName}`
  }

  return {
    providerFullName,
    jobTitle,
    providerProfileUrl,
    providerMetaDescription,
  }
}

export const generateSeFriendlyProviderSchemas = (
  props: PageSchemaProps
): {
  seFriendlyPersonTypeSchema: LdJsonSchema[]
  seFriendlyCatalogTypeSchema: unknown
} => {
  const { currentProviders, pageType, state, requestOrigin } = props

  const providerInfoMap = currentProviders.map((provider) => ({
    provider,
    ...getProviderInfo(provider, requestOrigin),
  }))

  const seFriendlyPersonTypeSchema = providerInfoMap.map(
    ({
      provider,
      providerFullName,
      jobTitle,
      providerProfileUrl,
      providerMetaDescription,
    }) => ({
      '@context': 'http://schema.org',
      '@type': 'Person',
      image: provider?.profileImagePath || '',
      url: providerProfileUrl,
      name: providerFullName,
      jobTitle,
      description: providerMetaDescription,
      address: {
        '@type': 'PostalAddress',
        ...(pageType !== 'state' && { addressLocality: props.city }),
        addressRegion: stateAbbreviations[state],
        addressCountry: 'USA',
      },
    })
  )

  const seFriendlyCatalogTypeSchema = providerInfoMap.map(
    ({
      providerFullName,
      jobTitle,
      providerProfileUrl,
      providerMetaDescription,
    }) => ({
      '@type': 'Person',
      name: providerFullName,
      jobTitle,
      worksFor: {
        '@type': 'MedicalOrganization',
        name: 'Thriveworks',
      },
      description: providerMetaDescription,
      url: providerProfileUrl,
    })
  )

  return { seFriendlyPersonTypeSchema, seFriendlyCatalogTypeSchema }
}

export const generateSeFriendlyLdJsonSchema = (
  props: PageSchemaProps
): LdJsonSchema => {
  const {
    headerTitle,
    metaDescription,
    currentProviders,
    faqs,
    providerType,
    pageType,
    state,
    requestOrigin,
    requestPathname,
  } = props

  const origin =
    requestOrigin ??
    (typeof window !== 'undefined' ? window.location.origin : '')
  const pathname =
    requestPathname ??
    (typeof window !== 'undefined' ? window.location.pathname : '')

  const pageUrl = `${origin}${pathname}`
  const hubPageURL = `${origin}/${providerType}`

  let areaServed: string
  let serviceAudience: string
  let cityPageUrl: string

  const breadcrumbItems = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: 'https://thriveworks.com/',
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Find available therapists & counselors near you',
      item: hubPageURL,
    },
    {
      '@type': 'ListItem',
      position: 3,
      name: headerTitle,
      item: pageUrl,
    },
  ]

  switch (pageType) {
    case SeFriendlyPageType.STATE:
      areaServed = state
      serviceAudience = `Individuals seeking licensed therapists and counselors in ${state}`
      break

    case SeFriendlyPageType.CITY:
      areaServed = `${props.city}, ${state}`
      serviceAudience = `Individuals seeking licensed therapists and counselors in ${props.city}, ${state}`
      cityPageUrl = pageUrl
      break

    case SeFriendlyPageType.SPECIALTY:
      areaServed = `${props.city}, ${state}`
      serviceAudience = `Individuals seeking ${
        specialtyValueMapping[props.specialty]
      } in ${props.city}, ${state}`

      cityPageUrl =
        hubPageURL +
        `/${state.toLowerCase()}/${formatCity(props.city.toLowerCase())}`

      // Inserting the specialty-specific item at position 3 if pageType is 'specialty'
      breadcrumbItems.splice(2, 0, {
        '@type': 'ListItem',
        position: 3,
        name: `Therapists & Counselors Available Near ${props.city}, ${state}`,
        item: cityPageUrl,
      })

      breadcrumbItems[3].position = 4
      break
  }

  const seFriendlyLdJsonSchema = []

  const {
    seFriendlyPersonTypeSchema,
    seFriendlyCatalogTypeSchema,
  } = generateSeFriendlyProviderSchemas(props)

  const seFriendlySeviceTypeSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: headerTitle,
    description: metaDescription,
    url: pageUrl,
    provider: {
      '@type': 'MedicalOrganization',
      name: 'Thriveworks',
      description:
        'Thriveworks, clinician-founded and -led, is a leading mental health provider of therapy and psychiatry. We offer in-person and online care, with 340+ offices and 2,200+ clinicians across the US.',
      email: 'support@thriveworks.com',
    },
    areaServed,
    serviceAudience: {
      '@type': 'Audience',
      name: serviceAudience,
    },
    offers: {
      '@type': 'Offer',
      price: 'Variable',
      priceCurrency: 'USD',
      url: pageUrl,
    },
    providerMobility: 'https://schema.org/Online',
    availableChannel: {
      '@type': 'ServiceChannel',
      serviceLocation: {
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
          ...(pageType !== 'state' && { addressLocality: props.city }),
          addressRegion: state,
          addressCountry: 'US',
        },
      },
    },
    ...(currentProviders.length > 0 && {
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        itemListElement: seFriendlyCatalogTypeSchema,
      },
    }),
  }

  seFriendlyLdJsonSchema.push(
    seFriendlySeviceTypeSchema,
    ...seFriendlyPersonTypeSchema
  )

  if (faqs) {
    const formattedFaqData = faqs.map(({ question, answer }) => {
      const formattedanswers = convertHtmlToString(answer)

      return {
        '@type': 'Question',
        name: question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: formattedanswers,
        },
      }
    })

    const seFriendlyFAQSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: formattedFaqData,
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbItems,
      },
    }

    seFriendlyLdJsonSchema.push(seFriendlyFAQSchema)
  }

  return seFriendlyLdJsonSchema
}

const SCHEMA_FRAGMENT = {
  WEBPAGE: '#webpage',
  SERVICE: '#service',
  PROVIDER_LIST: '#provider-list',
  FAQ: '#faq',
  BREADCRUMB: '#breadcrumb',
  PERSON: '#person',
} as const

const capitalizeProviderType = (providerType: string): string =>
  providerType.charAt(0).toUpperCase() + providerType.slice(1)

const getCollectionPageSpecialtyLabel = (
  specialty: string,
  city: string,
  state: string
): string => {
  const breadcrumbText = specialtyValueMappingForBreadcrumbs[specialty]

  return breadcrumbText
    ? `${breadcrumbText} in ${city}, ${state}`
    : `Therapists in ${city}, ${state}`
}

const buildCollectionPageBreadcrumbItems = (
  props: CollectionPageSchemaProps,
  origin: string,
  canonicalUrl: string,
  stateName: string
): LdJsonSchemaObject[] => {
  const { providerType, pageType, state, city } = props
  const hubLabel = capitalizeProviderType(providerType)
  const hubUrl = `${origin}/${providerType}`
  const stateUrl = `${hubUrl}/${state.toLowerCase()}`
  const cityUrl = `${stateUrl}/${formatCity(city)}`
  const cityLabel = `${city}, ${state}`

  const breadcrumbItems: LdJsonSchemaObject[] = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: THRIVEWORKS_SCHEMA_HOME_URL,
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: hubLabel,
      item: hubUrl,
    },
    {
      '@type': 'ListItem',
      position: 3,
      name: stateName,
      item: stateUrl,
    },
    {
      '@type': 'ListItem',
      position: 4,
      name: cityLabel,
      item: pageType === SeFriendlyPageType.CITY ? canonicalUrl : cityUrl,
    },
  ]

  if (pageType === SeFriendlyPageType.SPECIALTY) {
    breadcrumbItems.push({
      '@type': 'ListItem',
      position: 5,
      name: getCollectionPageSpecialtyLabel(props.specialty, city, state),
      item: canonicalUrl,
    })
  }

  return breadcrumbItems
}

export const generateSeFriendlyCollectionPageLdJsonSchema = (
  props: CollectionPageSchemaProps
): LdJsonSchemaObject => {
  const {
    providerType,
    pageType,
    metaTitle,
    headerTitle,
    metaDescription,
    faqSectionName,
    currentProviders,
    faqs,
    state,
    city,
    requestOrigin,
    requestPathname,
  } = props

  const origin =
    requestOrigin ??
    (typeof window !== 'undefined' ? window.location.origin : '')
  const pathname =
    requestPathname ??
    (typeof window !== 'undefined' ? window.location.pathname : '')

  const canonicalUrl = `${origin}${pathname}`
  const stateName = stateAbbreviations[state]
  const isSpecialtyPage = pageType === SeFriendlyPageType.SPECIALTY
  const isPsychiatrist = providerType === providerTypes.PSYCHIATRISTS
  const specialtyDisplayName = isSpecialtyPage
    ? specialtyValueMapping[props.specialty]
    : undefined

  let serviceType = isPsychiatrist ? 'Psychiatry' : 'Therapy'
  let audienceType = `Individuals seeking licensed therapists and counselors in ${city}, ${state}`

  if (isSpecialtyPage && specialtyDisplayName) {
    serviceType = specialtyDisplayName
    audienceType = `Individuals seeking ${specialtyDisplayName} in ${city}, ${state}`
  }

  const webpageId = `${canonicalUrl}${SCHEMA_FRAGMENT.WEBPAGE}`
  const serviceId = `${canonicalUrl}${SCHEMA_FRAGMENT.SERVICE}`
  const providerListId = `${canonicalUrl}${SCHEMA_FRAGMENT.PROVIDER_LIST}`
  const faqId = `${canonicalUrl}${SCHEMA_FRAGMENT.FAQ}`
  const breadcrumbId = `${canonicalUrl}${SCHEMA_FRAGMENT.BREADCRUMB}`

  const itemListElement = currentProviders.map((provider, index) => {
    const { providerFullName, providerProfileUrl } = getProviderInfo(
      provider,
      origin
    )

    return {
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Person',
        '@id': `${providerProfileUrl}${SCHEMA_FRAGMENT.PERSON}`,
        name: providerFullName,
        url: providerProfileUrl,
      },
    }
  })

  const faqMainEntity = faqs.map(({ question, answer }) => ({
    '@type': 'Question',
    name: question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: convertHtmlToString(answer),
    },
  }))

  const breadcrumbItems = buildCollectionPageBreadcrumbItems(
    props,
    origin,
    canonicalUrl,
    stateName
  )

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        '@id': webpageId,
        url: canonicalUrl,
        name: metaTitle,
        description: metaDescription,
        isPartOf: {
          '@id': THRIVEWORKS_SCHEMA_WEBSITE_ID,
        },
        publisher: {
          '@type': 'Organization',
          '@id': THRIVEWORKS_SCHEMA_ORGANIZATION_ID,
        },
        mainEntity: {
          '@id': providerListId,
        },
        about: {
          '@id': serviceId,
        },
        breadcrumb: {
          '@id': breadcrumbId,
        },
        hasPart: {
          '@id': faqId,
        },
        inLanguage: 'en-US',
      },
      {
        '@type': 'Service',
        '@id': serviceId,
        url: canonicalUrl,
        name: headerTitle,
        description: metaDescription,
        serviceType,
        provider: {
          '@type': 'Organization',
          '@id': THRIVEWORKS_SCHEMA_ORGANIZATION_ID,
        },
        areaServed: {
          '@type': 'City',
          name: city,
          containedInPlace: {
            '@type': 'State',
            name: stateName,
          },
        },
        audience: {
          '@type': 'PeopleAudience',
          audienceType,
        },
        mainEntityOfPage: {
          '@id': webpageId,
        },
      },
      {
        '@type': 'ItemList',
        '@id': providerListId,
        name: headerTitle,
        numberOfItems: currentProviders.length,
        itemListOrder: SCHEMA_ORG_ITEM_LIST_ORDER_UNORDERED,
        itemListElement,
      },
      {
        '@type': 'FAQPage',
        '@id': faqId,
        name: faqSectionName,
        isPartOf: {
          '@id': webpageId,
        },
        mainEntity: faqMainEntity,
      },
      {
        '@type': 'BreadcrumbList',
        '@id': breadcrumbId,
        itemListElement: breadcrumbItems,
      },
    ],
  }
}

const HUB_SCHEMA_FRAGMENT = {
  WEBPAGE: '#webpage',
  SERVICE: '#service',
  POPULAR_CITIES: '#popular-cities',
  AVAILABLE_STATES: '#available-states',
  FAQ: '#faq',
  BREADCRUMB: '#breadcrumb',
} as const

/**
 * Visible popular-city links for a hub page, filtered to states that currently
 * have availability (same set rendered by HubStates).
 */
export const getVisiblePopularCities = (
  providerType: string,
  validStates: Record<string, string[]>
): HubPopularCityLink[] => {
  const cities =
    providerType === providerTypes.PSYCHIATRISTS
      ? popularCities.psychiatrists
      : popularCities.therapists

  const validStatesLower = Object.keys(validStates).map((state) =>
    state.toLowerCase()
  )

  return Object.entries(cities)
    .filter(([key]) => validStatesLower.includes(key))
    .flatMap(([state, citiesArr]) =>
      citiesArr.map((city) => ({
        name: `${city}, ${state.toUpperCase()}`,
        path: `/${providerType}/${state}/${formatCity(city)}`,
      }))
    )
}

const getSortedAvailableStateCodes = (
  validStates: Record<string, string[]>
): string[] =>
  Object.keys(validStates)
    .filter((stateCode) => stateAbbreviations[stateCode.toUpperCase()])
    .sort((stateCode1, stateCode2) =>
      stateAbbreviations[stateCode1.toUpperCase()].localeCompare(
        stateAbbreviations[stateCode2.toUpperCase()]
      )
    )

/**
 * Builds the Nectiv hub-page JSON-LD `@graph` for `/therapists` and
 * `/psychiatrists`: CollectionPage, Service, popular-cities ItemList,
 * available-states ItemList, BreadcrumbList, and FAQPage.
 */
export const generateSeFriendlyHubPageLdJsonSchema = (
  props: HubPageSchemaProps
): LdJsonSchemaObject => {
  const {
    providerType,
    metaTitle,
    metaDescription,
    serviceName,
    serviceDescription,
    serviceType,
    popularCitiesListName,
    availableStatesListName,
    faqSectionName,
    faqs,
    validStates,
    directoryListItemLinkKey,
    requestOrigin,
    requestPathname,
    dateModified,
  } = props

  const origin =
    requestOrigin ??
    (typeof window !== 'undefined' ? window.location.origin : '')
  const pathname =
    requestPathname ??
    (typeof window !== 'undefined' ? window.location.pathname : '')

  const canonicalUrl = `${origin}${pathname}`
  const hubLabel = capitalizeProviderType(providerType)

  const webpageId = `${canonicalUrl}${HUB_SCHEMA_FRAGMENT.WEBPAGE}`
  const serviceId = `${canonicalUrl}${HUB_SCHEMA_FRAGMENT.SERVICE}`
  const popularCitiesId = `${canonicalUrl}${HUB_SCHEMA_FRAGMENT.POPULAR_CITIES}`
  const availableStatesId = `${canonicalUrl}${HUB_SCHEMA_FRAGMENT.AVAILABLE_STATES}`
  const faqId = `${canonicalUrl}${HUB_SCHEMA_FRAGMENT.FAQ}`
  const breadcrumbId = `${canonicalUrl}${HUB_SCHEMA_FRAGMENT.BREADCRUMB}`

  const visiblePopularCities = getVisiblePopularCities(
    providerType,
    validStates
  )
  const availableStateCodes = getSortedAvailableStateCodes(validStates)

  const popularCityListElements = visiblePopularCities.map((city, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: city.name,
    [directoryListItemLinkKey]: `${origin}${city.path}`,
  }))

  const availableStateListElements = availableStateCodes.map(
    (stateCode, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: stateAbbreviations[stateCode.toUpperCase()],
      [directoryListItemLinkKey]: `${origin}/${providerType}/${stateCode.toLowerCase()}`,
    })
  )

  const faqMainEntity = faqs.map(({ question, answer }) => ({
    '@type': 'Question',
    name: question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: convertHtmlToString(answer),
    },
  }))

  const breadcrumbItems = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: THRIVEWORKS_SCHEMA_HOME_URL,
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: hubLabel,
      item: canonicalUrl,
    },
  ]

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        '@id': webpageId,
        url: canonicalUrl,
        name: metaTitle,
        description: metaDescription,
        isPartOf: {
          '@id': THRIVEWORKS_SCHEMA_WEBSITE_ID,
        },
        publisher: {
          '@type': 'Organization',
          '@id': THRIVEWORKS_SCHEMA_ORGANIZATION_ID,
        },
        about: {
          '@id': serviceId,
        },
        mainEntity: [
          {
            '@id': popularCitiesId,
          },
          {
            '@id': availableStatesId,
          },
        ],
        breadcrumb: {
          '@id': breadcrumbId,
        },
        hasPart: {
          '@id': faqId,
        },
        inLanguage: 'en-US',
        ...(dateModified ? { dateModified } : {}),
      },
      {
        '@type': 'Service',
        '@id': serviceId,
        url: canonicalUrl,
        name: serviceName,
        description: serviceDescription,
        serviceType,
        provider: {
          '@type': 'Organization',
          '@id': THRIVEWORKS_SCHEMA_ORGANIZATION_ID,
        },
      },
      {
        '@type': 'ItemList',
        '@id': popularCitiesId,
        name: popularCitiesListName,
        numberOfItems: visiblePopularCities.length,
        itemListOrder: SCHEMA_ORG_ITEM_LIST_ORDER_UNORDERED,
        itemListElement: popularCityListElements,
      },
      {
        '@type': 'ItemList',
        '@id': availableStatesId,
        name: availableStatesListName,
        numberOfItems: availableStateCodes.length,
        itemListOrder: SCHEMA_ORG_ITEM_LIST_ORDER_UNORDERED,
        itemListElement: availableStateListElements,
      },
      {
        '@type': 'BreadcrumbList',
        '@id': breadcrumbId,
        itemListElement: breadcrumbItems,
      },
      {
        '@type': 'FAQPage',
        '@id': faqId,
        name: faqSectionName,
        isPartOf: {
          '@id': webpageId,
        },
        mainEntity: faqMainEntity,
      },
    ],
  }
}
